import { useState } from "react";
import { CheckSquare, Check, Undo2, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import { useApprovals, useCreateApproval, useResolveApproval } from "@/hooks/usePlanner";

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-coral-light text-coral-dark",
  approved: "bg-teal-light text-teal-dark",
  changes_requested: "bg-destructive/10 text-destructive",
};
const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  changes_requested: "Changes requested",
};

export default function PlannerApprovalsPage() {
  const clients = useWorkspaceStore((s) => s.workspaces).filter((w) => w.website);
  const { data: approvals = [], isLoading } = useApprovals();
  const createApproval = useCreateApproval();
  const resolve = useResolveApproval();

  const now = new Date();
  const [client, setClient] = useState("");
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [note, setNote] = useState("");

  const send = () => {
    if (!client) return;
    createApproval.mutate(
      { tenant_id: client, billing_period: period, note },
      { onSuccess: () => setNote("") },
    );
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <CheckSquare className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Approvals</h1>
          <p className="text-sm text-muted-foreground">Send a client's monthly plan for approval, then approve or request changes.</p>
        </div>
      </div>

      {/* Send for approval */}
      <Card className="border-0 shadow-sm">
        <div className="flex flex-wrap items-end gap-2 px-4 py-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Client</label>
            <Select value={client || undefined} onValueChange={setClient}>
              <SelectTrigger className="h-9 w-56"><SelectValue placeholder="Choose a client…" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.website} value={c.website as string}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Month</label>
            <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM" className="h-9 w-28" />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Note (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything to flag for review…" className="h-9 min-w-[200px]" />
          </div>
          <Button onClick={send} disabled={!client || createApproval.isPending} className="h-9">
            <Plus className="mr-1 h-4 w-4" /> Send for approval
          </Button>
        </div>
      </Card>

      {/* Queue */}
      <Card className="border-0 shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20">
                <TableHead className="min-w-[200px]">Client</TableHead>
                <TableHead className="w-[110px]">Month</TableHead>
                <TableHead className="w-[160px]">Status</TableHead>
                <TableHead className="min-w-[180px]">Note</TableHead>
                <TableHead className="w-[220px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
              ) : approvals.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">Nothing awaiting approval.</TableCell></TableRow>
              ) : (
                approvals.map((a) => (
                  <TableRow key={a.id} className={cn(a.status !== "pending" && "opacity-80")}>
                    <TableCell className="font-medium">{a.tenant_id}</TableCell>
                    <TableCell className="tabular-nums">{a.billing_period}</TableCell>
                    <TableCell>
                      <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold", STATUS_STYLE[a.status])}>
                        {STATUS_LABEL[a.status]}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[240px] truncate text-muted-foreground" title={a.note}>{a.note || "—"}</TableCell>
                    <TableCell className="text-right">
                      {a.status === "pending" ? (
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" className="h-8" onClick={() => resolve.mutate({ id: a.id, status: "approved" })}>
                            <Check className="mr-1 h-3.5 w-3.5" /> Approve
                          </Button>
                          <Button variant="outline" size="sm" className="h-8" onClick={() => resolve.mutate({ id: a.id, status: "changes_requested" })}>
                            <Undo2 className="mr-1 h-3.5 w-3.5" /> Changes
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">by {a.decided_by || "—"}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
